"use client";
import React, { useEffect, useMemo, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { Plus, Minus, ShoppingCart, Star, ChevronDown } from "lucide-react";
import CancelIcon from "@mui/icons-material/Cancel";
import { findPDPqty } from "../../data";
import { setSelectedVariant } from "../../features/productDesc/productDescSlice";
import { setSelectedColor } from "../../store/slices/colorSlice";
import {
  addToCartsAsync,
  fetchAllCartsAsync,
  removeFromCartsAsync,
  selectAllCarts,
} from "../../features/cartPage/cartPageSlice";
import { selectIsOverlayVisible, toggleOverlay } from "../../store/slices/headerSlice";
import { calculateDealerPrice, calculateUpdatedDiscountPercentage, capitalizeWords, colourNameToHex } from "../../utils/utils";
import { trackEvent } from "../../analytics/gtag";
import { EVENTS } from "../../analytics/events";

import { selectUser } from "../../features/auth/authSlice";
import SwipeToAction from "./SwipeToAction";
import MobilePriceBreakDown from "./MobilePriceBreakDown";

const AdvantageItem = ({ image, text }) => (
  <div className="flex items-center justify-start mr-1 mb-1 p-1 bg-gray-200 rounded-[25px] max-w-[250px] h-[30px]">
    <img
      loading="lazy"
      src={`https://cyclecircle.blr1.cdn.digitaloceanspaces.com/${process.env.REACT_APP_IMAGE_BASE}_ADVANTAGE/${image}`}
      alt={text}
      className="w-[25px] h-[25px] rounded-full p-[2px] mr-1 flex-shrink-0"
    />
    <span className="text-[14px] font-sans font-semibold text-black/80 truncate overflow-hidden whitespace-nowrap flex-1 leading-none">
      {text}
    </span>
  </div>
);



function MobileVariantSelection({ selectProduct, qtys, toggleOverlay, handleCloseSheet, isBottomSheetOpen }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const selectedVariant = useSelector(
    (state) => state.productDetail.selectedVariant
  );
  const userDetail = useSelector(selectUser);
  const cart = useSelector(selectAllCarts);

  const [loading, setLoading] = useState(false);
  const [cartVal, setCartVal] = useState(0);
  const [isCartValueZero, setIsCartValueZero] = useState(false);

  const [selectedSize, setSelectedSize] = useState(
    selectedVariant?.size_name || null
  );
  const [selectColor, setSelectColor] = useState(
    selectedVariant?.colour_name || null
  );
  const [availableSizes, setAvailableSizes] = useState([]);
  const [availableColors, setAvailableColors] = useState([]);

  const quantity = findPDPqty(selectedVariant?.SKU_ID, qtys && qtys) || 0;

  const advantages =
    selectProduct && selectProduct[0] && selectProduct[0]?.Advantages;

  const variations = useMemo(
    () =>
      (selectProduct &&
        selectProduct[0]?.skuDetails &&
        selectProduct[0]?.skuDetails) ||
      [],
    [selectProduct]
  );

  // ✅ Initial setup
  useEffect(() => {
    if (variations.length > 0) {
      const sizes = [...new Set(variations.map((v) => v.size_name))];
      const colors = [...new Set(variations.map((v) => v.colour_name))];
      setAvailableSizes(sizes);
      setAvailableColors(colors);
      dispatch(setSelectedVariant(variations[0]));
    }
  }, [variations, dispatch]);

  // ✅ Auto-adjust quantity based on available stock
  useEffect(() => {
    if (selectedVariant && qtys) {
      const availableQty = findPDPqty(selectedVariant?.SKU_ID, qtys);
      if (cartVal > availableQty) setCartVal(availableQty);
    }
  }, [selectedVariant, qtys, cartVal]);

  // ✅ Filter logic
  useEffect(() => {
    if (!variations.length) return;

    let filteredSizes = [...new Set(variations.map((v) => v.size_name))];
    let filteredColors = [...new Set(variations.map((v) => v.colour_name))];

    if (selectedSize && selectColor) {
      const selectedSku = variations.find(
        (v) =>
          v.size_name === selectedSize && v.colour_name === selectColor
      );
      if (selectedSku) dispatch(setSelectedVariant(selectedSku));
    } else if (selectedSize) {
      filteredColors = variations
        .filter((v) => v.size_name === selectedSize)
        .map((v) => v.colour_name);
    } else if (selectColor) {
      filteredSizes = variations
        .filter((v) => v.colour_name === selectColor)
        .map((v) => v.size_name);
    }

    setAvailableSizes([...new Set(filteredSizes)]);
    setAvailableColors([...new Set(filteredColors)]);
  }, [selectedSize, selectColor, variations, dispatch]);

  // ✅ Handlers for Size/Color selection
  const handleSelectSize = (size) =>
    setSelectedSize(selectedSize === size ? null : size);
  const handleSelectColor = (color) => {
    dispatch(setSelectedColor(color));
    setSelectColor(selectColor === color ? null : color);
  };

  // ✅ Quantity controls
  const bundleSize = parseInt(selectProduct?.[0]?.Bundle || "1");
  const availableQty = findPDPqty(selectedVariant?.SKU_ID, qtys);

  const increaseCarts = () => {
    if (cartVal + bundleSize <= availableQty) setCartVal(cartVal + bundleSize);
  };

  const decreaseCarts = () => {
    if (cartVal >= bundleSize) setCartVal(cartVal - bundleSize);
  };

  const handleInputChange = (e) => {
    const inputValue = parseInt(e.target.value) || 0;
    const maxQty = findPDPqty(selectedVariant?.SKU_ID, qtys);
    setCartVal(Math.min(inputValue, maxQty));
  };

  // ✅ Add/Remove Cart Actions
  const handleOpen = () => setLoading(true);
  const handleClose = () => setLoading(false);

  const isInCart = () =>
    Array.isArray(cart) &&
    cart?.some((i) => i?.skuDetails?.SKU_ID === selectedVariant?.SKU_ID);

  const addToCart = () => {
    handleOpen();
    const cartData = {
      userID: userDetail?.UserID,
      SKU: selectedVariant?.SKU_ID,
      buyQty: cartVal || bundleSize,
    };
    if (userDetail?.UserID && cartVal >= 1) {
      dispatch(addToCartsAsync(cartData)).then(() => {
        trackEvent({
          action: EVENTS.ADD_TO_CART,
          category: "Cart",
          label: `SKU: ${selectedVariant?.SKU_ID}`,
          value: userDetail?.PhoneNumber,
        });
        dispatch(fetchAllCartsAsync(userDetail.UserID)).then(() => {
          handleClose();
        });
      });
    } else {
      setIsCartValueZero(true);
      handleClose();
    }
  };

  const removeFromCart = () => {
    handleOpen();
    const cartData = {
      userID: userDetail?.UserID,
      SKU: selectedVariant?.SKU_ID,
    };
    dispatch(removeFromCartsAsync(cartData)).then(() => {
      trackEvent({
        action: EVENTS.REMOVE_FROM_CART,
        category: "Cart",
        label: `SKU: ${selectedVariant?.SKU_ID}`,
        value: userDetail?.PhoneNumber,
      });
      dispatch(fetchAllCartsAsync(userDetail.UserID)).then(() => {
        handleClose();
      });
    });
  };

  const buyNowHandle = () => {
    if (isInCart()) {
      navigate("/carts");
    } else {
      addToCart();
      navigate("/carts");
    }
  };


  // ▼▼ Add inside component, near other state hooks ▼▼
  const [showPriceDetails, setShowPriceDetails] = useState(false);

  const totalAmount = useMemo(() => {
    if (!selectedVariant) return 0;
    const price = calculateDealerPrice(
      parseInt(selectedVariant?.MRP_price),
      parseInt(selectedVariant?.NDP_Price),
      selectProduct?.[0]?.GST
    );
    return (price * cartVal).toFixed(2);
  }, [selectedVariant, cartVal, selectProduct]);


  return (
    <>
      {/* Backdrop Loader */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Variant Content */}
      <div className="w-full bg-white-1 rounded-t-3xl px-3 pt-2 pb-3 text-black-1 z-[1200]">
        <div className="w-full h-[40px] border-b-2 border-black-1/15 flex items-center justify-between px-3">
          <span className="text-16 font-bold text-blue">Select Variant</span>
          <CancelIcon
            onClick={handleCloseSheet}
            className="text-blue"
            style={{ fontSize: "20px" }}
          />
        </div>
        {/* Header / Title */}
        <div className="flex items-start gap-4 border-b border-gray-200 pb-2 my-2">
          {/* Product Image */}
          <div className="flex-shrink-0 w-[100px] h-[90px] rounded-sm overflow-hidden bg-gray-50 flex items-center justify-center shadow-sm">
            <img
              src={`https://cyclecircle.blr1.cdn.digitaloceanspaces.com/${process.env.REACT_APP_IMAGE_BASE}_PRODUCTS_IMAGE/${selectProduct?.[0]?.images?.[0]?.[0]}`}
              alt={selectedVariant?.prod_name}
              className="w-full h-full object-contain transition-transform duration-300"
            />
          </div>

          {/* Product Details */}
          <div className="flex flex-col flex-1">
            {/* Manufacturer */}
            <div className="flex items-center">
              {selectProduct && selectProduct[0]?.manufacture_images ? (
                <img
                  loading="lazy"
                  className="h-5 object-contain"
                  src={`https://cyclecircle.blr1.cdn.digitaloceanspaces.com/${process.env.REACT_APP_IMAGE_BASE}_MANUFACTURE_IMAGE/${selectProduct[0]?.manufacture_images}`}
                  alt="brand-logo"
                />
              ) : (
                <span className="text-[13px] text-gray-500 italic">
                  By {selectProduct[0]?.Manufacturer}
                </span>
              )}
            </div>
            <h2 className="mt-2 text-[15px] font-semibold leading-snug text-gray-900 line-clamp-2">
              {selectedVariant?.prod_name &&
                selectProduct?.[0]?.prod_name}
            </h2>

            {/* Pricing */}
            <div className="mt-1 w-full flex items-center  gap-5 justify-start">
              <div className="flex flex-col items-start justify-start">
                <p className="font-semibold text-[15px] text-gray-800">
                  Sale Price – <span className="text-[18px]">
                    ₹
                    {calculateDealerPrice(
                      parseInt(selectedVariant?.MRP_price),
                      parseInt(selectedVariant?.NDP_Price),
                      selectProduct?.[0]?.GST
                    ).toFixed(2)}
                  </span>
                </p>
                <p className="text-[13px] text-gray-500 mt-[2px]">
                  Product MRP – ₹ <span className="line-through">{selectedVariant?.MRP_price}</span>
                </p>
              </div>

              <p className="w-fit text-[12px] px-3 py-1 rounded-md bg-peach  font-semibold text-white-1">
                {calculateUpdatedDiscountPercentage(
                  selectedVariant?.MRP_price,
                  parseInt(selectedVariant?.NDP_Price),
                  selectProduct?.[0]?.GST
                )}
                % OFF
              </p>

            </div>
          </div>
        </div>
        {advantages && (
          <div
            className="w-full flex flex-col items-start justify-start py-1 pl-3 border-b-2 border-black-1/15"
          >
            <div className="w-full flex items-center justify-start flex-wrap mt-1">
              {advantages?.Advantage1 && (
                <AdvantageItem
                  image={advantages?.Image1}
                  text={advantages?.Advantage1}
                />
              )}
              {advantages?.Advantage2 && (
                <AdvantageItem
                  image={advantages?.Image2}
                  text={advantages?.Advantage2}
                />
              )}
              {advantages?.Advantage3 && (
                <AdvantageItem
                  image={advantages?.Image3}
                  text={advantages?.Advantage3}
                />
              )}
              {advantages?.Advantage4 && (
                <AdvantageItem
                  image={advantages?.Image4}
                  text={advantages?.Advantage4}
                />
              )}
            </div>
          </div>
        )}

        {/* Size Selection */}
        {availableSizes &&
          availableSizes[0] !== null &&
          availableSizes?.length > 0 && (
            <div className="mt-3">
              <p className="text-[14px] font-bold mb-2 text-gray-700">Choose Size:</p>
              <div className="flex gap-2 flex-wrap ml-2">
                {availableSizes?.map((size) => (
                  <button
                    key={size}
                    onClick={() => handleSelectSize(size)}
                    className={`px-4 py-1.5 text-sm rounded-[2px] border-2 transition-all duration-150 font-medium ${selectedSize === size
                      ? "border-peach text-black-1"
                      : "border-gray-300 text-gray-700 hover:border-gray-400"
                      }`}
                  >
                    {capitalizeWords(size)}
                  </button>
                ))}
              </div>
            </div>
          )}

        {/* Color Selection */}
        {availableColors &&
          availableColors[0] !== null &&
          availableColors?.length > 0 && (
            <div className="mt-3">
              <p className="text-[14px] font-bold mb-2 text-gray-700">Choose Color:</p>
              <div className="flex gap-3 items-center flex-wrap ml-2">
                {availableColors?.map((color) => (
                  <button
                    key={color}
                    onClick={() => handleSelectColor(color)}
                    className={`w-9 h-9 rounded-full flex items-center justify-center border transition-transform duration-150 ${selectColor === color
                      ? "scale-110 border-2 border-peach shadow-md"
                      : "border-none"
                      }`}
                    style={{
                      backgroundColor:
                        selectColor === color ? "white" : "transparent",
                    }}
                  >
                    <div
                      className="w-7 h-7 rounded-full"
                      style={{
                        backgroundColor: colourNameToHex(color),
                      }}
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

        <div className="flex flex-col items-start justify-start">
          <div className="mt-3">
            <p className="text-[14px] font-bold mb-2 text-gray-700">Choose Quantity:</p>
            {/* Quantity Selector */}
            <div className="flex items-center gap-3">
              <button
                onClick={decreaseCarts}
                className={`w-8 h-8 flex items-center justify-center rounded-[2px] transition-all duration-150 ${cartVal < bundleSize || quantity <= 0
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-blue text-white-1 hover:bg-blue"
                  }`}
              >
                <Minus size={18} />
              </button>

              <input
                type="number"
                disabled={quantity <= 0}
                className={`w-12 h-8 text-center border ${isCartValueZero ? "border-peach" : "border-gray-300"
                  } text-gray-800 rounded-[2px] focus:ring-2 focus:ring-blue outline-none ${quantity <= 0 ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""
                  }`}
                value={cartVal}
                onChange={handleInputChange}
              />

              <button
                onClick={increaseCarts}
                disabled={quantity <= 0}
                className={`w-8 h-8 flex items-center justify-center rounded-[2px] transition-all duration-150 ${quantity <= 0 || cartVal >= quantity
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-blue text-white-1 hover:bg-blue"
                  }`}
              >
                <Plus size={18} />
              </button>
            </div>
          </div>
          {/* Stock Status */}
          <div className="mt-3 flex flex-col items-start">
            {quantity <= 0 ? (
              <div className="flex items-center text-red-500 font-medium text-sm mb-2">
                <span className="mr-1">⚠️</span> Out of Stock
              </div>
            ) : cartVal >= quantity ? (
              <div className="flex flex-col items-start gap-2">
                <span className="text-xs text-gray-700 font-medium">
                  *Only {quantity} units in stock!
                </span>
                <button className="ml-2 px-3 whitespace-nowrap py-[3px] bg-orange-500 text-white text-xs rounded-full shadow-sm hover:bg-orange-600 transition-all duration-150">
                  Request more
                </button>
              </div>
            ) : (
              <div className="flex items-center text-green-600 font-semibold text-sm mb-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                In Stock
              </div>
            )}

          </div>
        </div>


        {/* PRICE ACCORDION */}
        <div className="w-full mt-4 mb-3 px-3 py-3 bg-white-1 border border-gray-200 rounded-xl shadow-sm">

          {/* Header Row */}
          <button
            onClick={() => setShowPriceDetails(!showPriceDetails)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <p className="text-[14px] text-gray-500 font-medium">Total Amount</p>
              <p className="text-[20px] font-bold text-gray-900 mt-[2px]">
                ₹{totalAmount}
              </p>
            </div>

            {/* Icons */}
            <div className="text-blue">
              {showPriceDetails ? (
                <ChevronDown size={24} />
              ) : (
                <Plus size={24} />
              )}
            </div>
          </button>

          {/* Animated Expand */}
          {showPriceDetails && (
            <div className="mt-4 border-t pt-3 animate-fadeIn">
              <MobilePriceBreakDown
                currentProduct={selectProduct}
              />
            </div>
          )}
        </div>

        <SwipeToAction
          addToCart={addToCart}
          removeFromCart={removeFromCart}
          isInCart={isInCart}
          buyNowHandle={buyNowHandle}
          toggleOverlay={toggleOverlay}
          userDetail={userDetail}
          isBottomSheetOpen={isBottomSheetOpen}
          isSwipeEnabled={
            (
              (!availableSizes || availableSizes?.filter(Boolean)?.length === 0) ||
              !!selectedSize
            ) &&
            (
              (!availableColors || availableColors?.filter(Boolean)?.length === 0) ||
              !!selectColor
            ) &&
            cartVal > 0
          }


        />

      </div>
    </>
  );
}

// Redux Connection
const mapStateToProps = (state) => ({
  isOverlayVisible: selectIsOverlayVisible(state),
});

const mapDispatchToProps = {
  toggleOverlay,
};

export default connect(mapStateToProps, mapDispatchToProps)(MobileVariantSelection);
